import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Skeleton } from '@/components/ui/skeleton'
import { MoreHorizontal, Edit, RefreshCw, Tag } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCreatorPrograms, useForceSyncCreatorProgram } from '@/hooks/use-creators'
import { ProgramFormDialog } from './program-form-dialog'
import { UI } from '@/lib/constants'
import type { CreatorProgram } from '@/lib/types'

export function ProgramListTable() {
  const { data: programs, isLoading } = useCreatorPrograms()
  const forceSync = useForceSyncCreatorProgram()

  const [selectedProgram, setSelectedProgram] = useState<CreatorProgram | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleEdit = (program: CreatorProgram) => {
    setSelectedProgram(program)
    setIsDialogOpen(true)
  }

  const handleCreate = () => {
    setSelectedProgram(null)
    setIsDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: UI.SKELETON_TABLE_ROWS }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  if (!programs || programs.length === 0) {
    return (
      <div className="flex h-[300px] shrink-0 items-center justify-center rounded-md border border-dashed">
        <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <Tag className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">Nenhum programa criado</h3>
          <p className="mb-4 mt-2 text-sm text-muted-foreground">
            Crie um programa para definir o desconto e regras dos cupons dos seus creators.
          </p>
          <ChonkyButton onClick={handleCreate}>Criar Programa</ChonkyButton>
        </div>
        <ProgramFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          program={selectedProgram}
        />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <ChonkyButton onClick={handleCreate}>Criar Programa</ChonkyButton>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Desconto</TableHead>
            <TableHead>Garantia</TableHead>
            <TableHead>Pedido Mín.</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {programs.map((program) => (
            <TableRow key={program.id}>
              <TableCell>
                <div>
                  <p className="font-medium">{program.name}</p>
                  {program.description && (
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {program.description}
                    </p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {program.rewardType === 'percentage'
                    ? `${program.rewardValue}%`
                    : `R$ ${program.rewardValue}`}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">
                {program.warrantyDays} {program.warrantyDays === 1 ? 'dia' : 'dias'}
              </TableCell>
              <TableCell className="text-sm">
                {program.minOrderValue ? `R$ ${program.minOrderValue}` : '—'}
              </TableCell>
              <TableCell>
                <Badge variant={program.isActive ? 'default' : 'secondary'}>
                  {program.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Abrir menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(program)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => forceSync.mutate(program.id)}
                      disabled={forceSync.isPending}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sincronizar Todos os Cupons
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ProgramFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        program={selectedProgram}
      />
    </div>
  )
}
